<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ChevronDown, ChevronRight } from 'lucide-vue-next';
import { Input } from '@/components/ui/input';
import { LazyTooltip } from '@/components/ui/tooltip';
import PlaylistContainerZoneLine from '@/components/DataTable/PlaylistContainerZoneLine.vue';
import { scheduleBadges, scheduleZoneNow, subscriberZoneBadges } from '@/components/arrayColumns/scheduleCell';
import { zoneSummaryFor } from '@/composables/api/useZoneSummaries';
import { zoneLineRowFor } from './zoneLineRow';
import { scheduleNow } from '@/composables/useScheduleClock';
import { zoneWallClock } from '@/composables/api/useTimeZones';
import type { ScheduleRole } from '@/utils/customPlaylistSchedule';
import ScheduleBadgePair from './ScheduleBadgePair.vue';
import ScheduleRow from './ScheduleRow.vue';
import {
    EXPANDED_GROUP_LIMIT,
    SCHEDULE_TAB_LABELS,
    asScheduleRows,
    buildScheduleGroups,
    defaultTab,
    filterGroups,
    positiveZoneId,
    tabCounts,
    toEntries,
    type ScheduleGroup,
    type ScheduleTab,
} from './scheduleGroups';

/**
 * A custom list's schedules, grouped by the zone that wrote them (decision
 * D2, mockup 3). Used by the table cell's drawer (density "line") and by the
 * Schedules section of the custom-list inspector (density "card").
 *
 * - Tabs: Active (the default), Upcoming (only when there is any), Expired
 *   and All. When nothing is active it opens on the first tab that has
 *   something, so a list whose only schedule has expired does not open empty.
 * - A filter box matches zone names, types, text and days ("fri").
 * - Owner first, then subscribers by name, then other zones. A list with
 *   schedules from one zone only drops the group chrome entirely.
 * - Past five groups the rest start collapsed to their header, with one row
 *   that expands them all.
 *
 * Seams for the editor stage (none of them render anything until used):
 *   #toolbar                          right of the counts, e.g. "Add schedule"
 *   #row-actions="{ schedule, summary, group, isOwner }"   per schedule
 *   #group-actions="{ group }"        in each group header
 */
const props = withDefaults(
    defineProps<{
        /** The list's nested `location_zone_custom_playlist_schedules`. */
        items: unknown;
        /** The list's own `location_zone_id`. */
        ownerZoneId: unknown;
        ownerZoneName?: string | null;
        density?: 'line' | 'card';
        /** The body scrolls on its own (the drawer) rather than with the page (the inspector). */
        scrollBody?: boolean;
        /** A schedule to point at. Its tab is opened, its zone expanded, and it is highlighted. */
        highlightScheduleId?: string | null;
    }>(),
    { ownerZoneName: null, density: 'line', scrollBody: false, highlightScheduleId: null },
);

const ownerId = computed(() => positiveZoneId(props.ownerZoneId));
const schedules = computed(() => asScheduleRows(props.items));

/**
 * "Now" on the owner zone's clock (amendment A6). Asking for the owner zone
 * here is one cached request per list; until it answers, the browser clock.
 */
const now = computed(() => {
    const lookup = ownerId.value !== null && schedules.value.length > 0 ? zoneSummaryFor(ownerId.value) : null;
    const instant = scheduleNow();
    return zoneWallClock(instant, lookup) ?? new Date(instant.getTime());
});

/**
 * Active, upcoming and expired are judged on each schedule's own zone clock,
 * because that zone's player plays it. The zone lookup that groups the rows
 * supplies the time zones; until then, the owner zone's clock.
 */
const entries = computed(() => {
    const instant = scheduleNow();
    const ownerRow = { location_zone_id: ownerId.value };
    return toEntries(schedules.value, now.value, undefined, (schedule) => scheduleZoneNow(schedule, ownerRow, instant));
});
const counts = computed(() => tabCounts(entries.value));

const groups = computed(() =>
    buildScheduleGroups(entries.value, {
        ownerZoneId: ownerId.value,
        ownerZoneName: props.ownerZoneName,
        lookupZone: zoneSummaryFor,
        now: now.value,
    }),
);

// The counts take every schedule, whatever its state, so together they equal the
// All tab's count, less any schedule on a zone the lookup cannot place.
const badges = computed(() =>
    scheduleBadges(schedules.value as Record<string, unknown>[], { location_zone_id: ownerId.value }),
);

// The default follows the data until the person picks a tab themselves.
const pickedTab = ref<ScheduleTab | null>(null);
const activeTab = computed<ScheduleTab>(() => pickedTab.value ?? defaultTab(counts.value));

const tabs = computed(() =>
    (['in-force', 'upcoming', 'expired', 'all'] as ScheduleTab[])
        .filter((tab) => tab !== 'upcoming' || counts.value.upcoming > 0 || activeTab.value === 'upcoming')
        .map((tab) => ({ value: tab, label: SCHEDULE_TAB_LABELS[tab], count: counts.value[tab] })),
);

const filterText = ref('');
const visibleGroups = computed(() => filterGroups(groups.value, activeTab.value, filterText.value));

/**
 * Zone headings show whenever a schedule's zone is not the list's own, so the zone is always
 * named by its linked zone line. Only a list whose schedules are all in its own zone goes
 * without them.
 */
const showGroupChrome = computed(() => groups.value.length > 1 || groups.value.some((group) => group.role !== 'owner'));

// Explicit open/closed choices, by group key. Anything not chosen follows its position.
const toggled = ref<Record<string, boolean>>({});

function isExpanded(group: ScheduleGroup, index: number): boolean {
    if (!showGroupChrome.value) return true;
    return toggled.value[group.key] ?? index < EXPANDED_GROUP_LIMIT;
}

function toggleGroup(group: ScheduleGroup, index: number): void {
    toggled.value = { ...toggled.value, [group.key]: !isExpanded(group, index) };
}

const collapsedRest = computed(() => {
    if (!showGroupChrome.value) return null;
    const rest = visibleGroups.value.slice(EXPANDED_GROUP_LIMIT).filter((group, offset) => !isExpanded(group, EXPANDED_GROUP_LIMIT + offset));
    if (rest.length === 0) return null;
    return {
        groups: rest,
        zoneCount: rest.length,
        scheduleCount: rest.reduce((total, group) => total + group.entries.length, 0),
    };
});

function expandRest(): void {
    const next = { ...toggled.value };
    for (const group of collapsedRest.value?.groups ?? []) {
        next[group.key] = true;
    }
    toggled.value = next;
}

const RAIL_CLASSES: Record<ScheduleRole | 'unresolved', string> = {
    owner: 'border-accent-border',
    subscriber: 'border-info/50',
    other: 'border-border',
    unresolved: 'border-border',
};

function roleKey(group: ScheduleGroup): ScheduleRole | 'unresolved' {
    return group.role ?? 'unresolved';
}

/**
 * Show the highlighted schedule: on its own tab when the one open does not hold it, with the
 * filter cleared and its zone expanded. It waits for the schedules, because an inspector that
 * fetched its list gets them after it opens. Each highlight is shown once: a later copy of the
 * list, such as the live poll's, must not undo a tab or a collapse the user chose since.
 *
 * The glow is shown once too. Showing a highlight sets glowingKey to its schedule, and the row
 * clears it when its glow starts. A row is drawn again when its zone is collapsed and expanded,
 * or when the tab changes. Schedule 9001 then does not glow again, because glowingKey is null.
 * Pointing at schedule 9001 again, after pointing at another one or at none, glows it again.
 */
let shownHighlightId: string | null = null;
const glowingKey = ref<string | null>(null);
watch(
    [() => props.highlightScheduleId, entries],
    ([highlightId]) => {
        if (highlightId === null) {
            shownHighlightId = null;
            glowingKey.value = null;
            return;
        }
        if (highlightId === shownHighlightId) return;
        const entry = entries.value.find((candidate) => candidate.key === highlightId);
        if (!entry) return;
        shownHighlightId = highlightId;
        glowingKey.value = highlightId;
        if (activeTab.value !== 'all' && entry.summary.state !== activeTab.value) pickedTab.value = entry.summary.state;
        filterText.value = '';
        const group = groups.value.find((candidate) => candidate.entries.some((groupEntry) => groupEntry.key === highlightId));
        if (group) toggled.value = { ...toggled.value, [group.key]: true };
    },
    { immediate: true },
);

/** "1 schedule in this zone", "3 schedules in this zone": the count beside a zone heading. */
function groupCountText(group: ScheduleGroup): string {
    const count = group.entries.length;
    return `${count.toLocaleString()} ${count === 1 ? 'schedule' : 'schedules'} in this zone`;
}

const emptyText = computed(() => {
    if (schedules.value.length === 0) return 'No schedules';
    if (filterText.value.trim() !== '') return `No schedules match “${filterText.value.trim()}”.`;
    return `No ${SCHEDULE_TAB_LABELS[activeTab.value].toLowerCase()} schedules.`;
});

/** The row the group heading's zone line reads, the same row the card and the editor use. */
function zoneLineRow(group: ScheduleGroup): Record<string, unknown> {
    return zoneLineRowFor(group.zoneId, group.zoneName) ?? { location_zone: { id: null, display_string: group.zoneName, location: null } };
}

/**
 * How many zones subscribe to this group's zone, as on the card. Not for a
 * subscriber zone: a subscriber never has subscribers of its own.
 */
function groupSubscriberCount(group: ScheduleGroup) {
    if (group.zoneId === null || group.role === 'subscriber') return [];
    return subscriberZoneBadges(group.zoneId);
}
</script>

<template>
    <div class="flex min-h-0 flex-col" :class="scrollBody ? 'flex-1 overflow-hidden' : ''" data-testid="schedule-grouped-list" :data-density="density">
        <div class="flex flex-col gap-2.5" :class="scrollBody ? 'border-b px-4 py-3' : 'pb-3'">
            <!-- Tabs on the left, counts and the toolbar on the right; in a narrow drawer the right side wraps under. -->
            <div class="flex min-w-0 flex-wrap items-center gap-2">
                <!-- Toggle buttons, not ARIA tabs: there is no tabpanel or arrow-key roving to promise. -->
                <!-- The app's segmented toggle, styled exactly as the queue's density toggle. -->
                <div v-if="schedules.length > 0" class="bg-muted inline-flex flex-wrap items-center rounded-lg p-[3px] text-xs" role="group" aria-label="Which schedules">
                    <button
                        v-for="tab in tabs"
                        :key="tab.value"
                        type="button"
                        class="cursor-pointer rounded-md px-2.5 py-1 font-medium transition-colors"
                        :class="tab.value === activeTab ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'"
                        :aria-pressed="tab.value === activeTab ? 'true' : 'false'"
                        :data-tab="tab.value"
                        data-testid="schedule-tab"
                        @click="pickedTab = tab.value"
                    >
                        {{ tab.label }} {{ tab.count }}
                    </button>
                </div>
                <div class="ml-auto flex items-center gap-2">
                    <ScheduleBadgePair :badges="badges" layout="joined" data-testid="schedule-counts" />
                    <slot name="toolbar" />
                </div>
            </div>
            <Input v-if="schedules.length > 1" v-model="filterText" placeholder="Filter by zone or day…" data-testid="schedule-filter" />
        </div>

        <div :class="scrollBody ? 'min-h-0 flex-1 overflow-y-auto' : ''" data-testid="schedule-groups">
            <div v-if="visibleGroups.length === 0" class="text-muted-foreground px-3 py-6 text-center text-sm" data-testid="schedule-empty">
                {{ emptyText }}
            </div>

            <template v-for="(group, index) in visibleGroups" :key="group.key">
                <section
                    class="flex flex-col"
                    :class="showGroupChrome && density === 'line' ? 'border-b last:border-b-0' : ''"
                    data-testid="schedule-group"
                    :data-group="group.key"
                    :data-role="group.role ?? 'unresolved'"
                    :data-expanded="isExpanded(group, index) ? 'true' : 'false'"
                >
                    <!-- line: a shaded header row, like the drawer mockup -->
                    <div
                        v-if="showGroupChrome && density === 'line'"
                        class="bg-muted-foreground/5 flex items-center gap-2 px-4 py-2 text-xs"
                        data-testid="schedule-group-header"
                    >
                        <button
                            type="button"
                            class="flex shrink-0 items-center gap-2 text-left"
                            :aria-expanded="isExpanded(group, index) ? 'true' : 'false'"
                            :aria-label="`${isExpanded(group, index) ? 'Collapse' : 'Expand'} ${group.zoneName}`"
                            @click="toggleGroup(group, index)"
                        >
                            <component :is="isExpanded(group, index) ? ChevronDown : ChevronRight" class="text-muted-foreground size-3 shrink-0" aria-hidden="true" />
                        </button>
                        <!-- The custom-list card's zone line and subscriber count, reused. -->
                        <span class="flex min-w-0 flex-1" data-testid="schedule-group-zone">
                            <PlaylistContainerZoneLine :row="zoneLineRow(group)" class="min-w-0 flex-1 text-xs" />
                        </span>
                        <ScheduleBadgePair
                            v-if="groupSubscriberCount(group).length"
                            class="shrink-0"
                            :badges="groupSubscriberCount(group)"
                            layout="bare"
                            data-testid="schedule-group-subscriber-count"
                        />
                        <slot name="group-actions" :group="group" />
                        <LazyTooltip side="top" align="end">
                            <span class="bg-primary/10 text-primary shrink-0 rounded border border-transparent px-2 py-0.5 text-xs font-medium cursor-default" data-testid="schedule-group-count">{{ group.entries.length }}</span>
                            <template #content><span data-testid="schedule-group-count-tooltip">{{ groupCountText(group) }}</span></template>
                        </LazyTooltip>
                    </div>

                    <!-- card: a small-caps section heading, like the cards-view mockup -->
                    <div
                        v-else-if="showGroupChrome"
                        class="text-muted-foreground flex items-center gap-2 pt-3 pb-1.5 font-mono text-[10px] tracking-wider uppercase"
                        data-testid="schedule-group-header"
                    >
                        <button
                            type="button"
                            class="flex shrink-0 items-center gap-1.5 text-left"
                            :aria-expanded="isExpanded(group, index) ? 'true' : 'false'"
                            :aria-label="`${isExpanded(group, index) ? 'Collapse' : 'Expand'} ${group.zoneName}`"
                            @click="toggleGroup(group, index)"
                        >
                            <component :is="isExpanded(group, index) ? ChevronDown : ChevronRight" class="size-3 shrink-0" aria-hidden="true" />
                        </button>
                        <!-- The custom-list card's zone line, reused. No subscriber count: the inspector's heading already shows it. -->
                        <span class="flex min-w-0 flex-1 font-sans normal-case tracking-normal" data-testid="schedule-group-zone">
                            <PlaylistContainerZoneLine :row="zoneLineRow(group)" class="min-w-0 flex-1 text-xs" />
                        </span>
                        <slot name="group-actions" :group="group" />
                        <LazyTooltip side="top" align="end">
                            <span class="bg-primary/10 text-primary shrink-0 rounded border border-transparent px-2 py-0.5 font-sans text-xs font-medium normal-case tracking-normal cursor-default" data-testid="schedule-group-count">{{ group.entries.length }}</span>
                            <template #content><span data-testid="schedule-group-count-tooltip">{{ groupCountText(group) }}</span></template>
                        </LazyTooltip>
                    </div>

                    <div
                        v-if="isExpanded(group, index)"
                        class="flex flex-col"
                        :class="[
                            density === 'line' ? 'gap-px' : 'gap-2',
                            density === 'line' && scrollBody ? 'px-2.5 py-1.5' : '',
                            showGroupChrome && density === 'line' ? ['border-l-2', RAIL_CLASSES[roleKey(group)]] : '',
                        ]"
                        data-testid="schedule-group-body"
                    >
                        <ScheduleRow
                            v-for="entry in group.entries"
                            :key="entry.key"
                            :schedule="entry.schedule"
                            :summary="entry.summary"
                            :now="now"
                            :density="density"
                            :highlighted="highlightScheduleId !== null && entry.key === highlightScheduleId"
                            :glow="glowingKey !== null && entry.key === glowingKey"
                            @glowed="glowingKey = null"
                        >
                            <template v-if="$slots['row-actions']" #actions="{ schedule, summary }">
                                <slot name="row-actions" :schedule="schedule" :summary="summary" :group="group" :is-owner="group.role === 'owner'" />
                            </template>
                        </ScheduleRow>
                    </div>
                </section>

                <button
                    v-if="collapsedRest && index === EXPANDED_GROUP_LIMIT - 1"
                    type="button"
                    class="text-muted-foreground hover:text-foreground flex w-full items-center gap-2 px-4 py-2 text-left text-xs"
                    data-testid="schedule-collapsed-note"
                    @click="expandRest"
                >
                    <ChevronRight class="size-3 shrink-0" aria-hidden="true" />
                    <span>
                        {{ collapsedRest.zoneCount }} more {{ collapsedRest.zoneCount === 1 ? 'zone' : 'zones' }}, collapsed —
                        {{ collapsedRest.scheduleCount }} {{ collapsedRest.scheduleCount === 1 ? 'schedule' : 'schedules' }}
                    </span>
                </button>
            </template>
        </div>
    </div>
</template>
